import { useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage, isApiError } from '../api'
import { Icon } from '../components/Icon'
import { Banner, Logo, Spinner } from '../components/ui'
import { qk } from '../hooks/queries'

/**
 * `/login/confirm?t=…`. The emailed link lands here (GET /auth/verify redirects without consuming the token),
 * and the token is only used when the owner presses the button. Email link scanners follow GET links, so
 * they can't use up a sign-in link. Never submits on its own.
 */
export function LoginConfirm() {
  const [params] = useSearchParams()
  const token = params.get('t') ?? ''
  const navigate = useNavigate()
  const qc = useQueryClient()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const verify = useMutation({
    mutationFn: () => api.verify(token),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.me })
      navigate('/app', { replace: true })
    },
  })
  const expired = !token || isApiError(verify.error, 400) || isApiError(verify.error, 410)

  useEffect(() => {
    if (expired) headingRef.current?.focus()
  }, [expired])

  return (
    <div className="flex min-h-screen flex-col">
      <header className="px-4 py-5 sm:px-8">
        <Logo />
      </header>
      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-8 sm:items-center sm:pt-0">
        <div className="card w-full max-w-[440px] p-6 sm:p-8">
          {expired ? (
            <div className="flex flex-col gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-bubble-owner text-owner-ink">
                <Icon name="clock" size={20} />
              </span>
              <h1 ref={headingRef} tabIndex={-1} className="font-head text-[26px] font-semibold tracking-[-0.6px] outline-none">
                That link has expired
              </h1>
              <p className="text-[15px] leading-6 text-muted">Sign-in links work for 15 minutes and only once. Ask for a new one and use it on this device.</p>
              <Link to="/login" className="btn btn-primary btn-lg">
                <Icon name="mail" size={16} /> Get a new sign-in link
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <h1 className="font-head text-[26px] font-semibold tracking-[-0.6px]">Sign in to Lobby</h1>
                <p className="text-[15px] leading-6 text-muted">You opened your sign-in link. Press the button to finish signing in on this device.</p>
              </div>
              {verify.isError && <Banner tone="error">{errorMessage(verify.error)}</Banner>}
              <button type="button" className="btn btn-primary btn-lg" disabled={verify.isPending || verify.isSuccess} onClick={() => verify.mutate()}>
                {verify.isPending || verify.isSuccess ? <Spinner /> : <Icon name="arrow-right" size={16} />}
                Sign in to Lobby
              </button>
              <p className="hint">Didn’t ask for this? Close the page; nothing happens until you press the button.</p>
            </div>
          )}
        </div>
      </main>
      <footer className="px-4 pb-6 text-center text-[13px] text-muted">
        <Link to="/" className="underline underline-offset-2">
          Back to the homepage
        </Link>
      </footer>
    </div>
  )
}
